/**
 * Conferência do ditado contra a transcrição literal (spec 009).
 *
 * POR QUE ISTO EXISTE — o pior defeito visto no teste de campo:
 *
 * Quando o áudio não dá conteúdo utilizável, o modelo **não devolve vazio**:
 * ele preenche os documentos com os EXEMPLOS que estão escritos no próprio
 * prompt ("Retinografia", "Facectomia com LIO", "Dorzolamida") e entrega tudo
 * com cara de pedido real. Uma receita inventada é internamente consistente —
 * passa por faixa, passo, catálogo e lateralidade sem disparar nada. O sistema
 * de lacunas, sozinho, é cego pra isso.
 *
 * A defesa é ancorar: o modelo devolve a transcrição literal do áudio, e todo
 * documento tem que se sustentar nela. O que não aparece no que foi dito não
 * vira documento — vira pendência.
 *
 * Isto NÃO substitui a conferência do médico; a transcrição fica na tela
 * justamente pra ele ver em um segundo que o ditado foi entendido.
 */

import type { VoiceDraft } from './resolveDrafts';
import type { VoiceGap } from './normalize';
import {
  EXAM_FINDING_WORDS,
  EXAM_MODULE_ALIASES,
  dropExamModules,
  examModuleLabel,
  isTextFindingField,
} from './examFill';

function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** Palavras curtas/genéricas não provam nada — "de", "e", "olho", "gota". */
const IGNORAR = new Set([
  'de', 'do', 'da', 'em', 'no', 'na', 'com', 'sem', 'para', 'por', 'ao', 'aos',
  'olho', 'olhos', 'ambos', 'direito', 'esquerdo', 'gota', 'gotas', 'colirio',
  'solucao', 'oftalmica', 'gel', 'pomada', 'gr', 'mg', 'ml', 'gr.', 'e',
  'gotejar', 'pingar', 'instilar', 'gramas', 'tratamento', 'cid',
]);

/**
 * Siglas do nome do documento — "Tomografia de Coerência Óptica (OCT)" → OCT.
 *
 * Existe porque o médico dita a SIGLA ("solicitação de OCT") e o catálogo
 * devolve o nome por extenso. Sem isto a ancoragem acusa de inventado
 * justamente o exame que ele pediu, com a sigla escrita na tela ao lado.
 * Siglas têm 2+ letras, então ficam fora do corte de 4 caracteres.
 */
function siglas(texto: string): string[] {
  return (texto.match(/\b[A-Z]{2,6}\b/g) ?? []).map((x) => x.toLowerCase());
}

/** Palavras da fala, pra casar sigla inteira (e não pedaço de outra palavra). */
function palavrasDaFala(fala: string): Set<string> {
  return new Set(fala.split(/[^a-z0-9]+/).filter(Boolean));
}

function termosSignificativos(texto: string): string[] {
  return norm(texto)
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 4 && !IGNORAR.has(t));
}

/**
 * O termo aparece no que foi dito?
 *
 * Comparação por prefixo de 4 letras: a transcrição escreve "Lacrifilm" e o
 * catálogo devolve "Lacrifilm Solução oftálmica"; "campimetria" vira "Campimetria
 * Computadorizada". Exigir igualdade exata reprovaria documento legítimo.
 */
function apareceNaFala(termo: string, fala: string): boolean {
  const t = norm(termo);
  if (t.length < 4) return false;
  return fala.includes(t.slice(0, Math.max(4, Math.floor(t.length * 0.7))));
}


/**
 * Aproximação fonética pt-BR: reduz a grafia ao som.
 *
 * Existe porque as duas defesas do ditado estavam brigando. O banco de
 * medicamentos corrige a fonética de propósito ("iabac" → Hyabak, "visuta" →
 * Vyzulta, "patanose" → Patanol S); a ancoragem então procurava "Hyabak" na
 * transcrição, não achava e acusava de inventado o medicamento CERTO.
 *
 * Regras: h mudo some, y↔i, z↔s, k/qu↔c, w↔v, ph↔f, letra dobrada vira uma.
 */
function fonetico(t: string): string {
  return norm(t)
    .replace(/ph/g, 'f')
    .replace(/qu/g, 'c')
    .replace(/[hy]/g, (m) => (m === 'y' ? 'i' : ''))
    .replace(/z/g, 's')
    .replace(/k/g, 'c')
    .replace(/w/g, 'v')
    .replace(/(.)\1+/g, '$1');
}

/** Distância de edição, cortando cedo quando já passou do limite. */
function distancia(a: string, b: string, limite: number): number {
  if (Math.abs(a.length - b.length) > limite) return limite + 1;
  let linha = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const nova = [i];
    let melhor = i;
    for (let j = 1; j <= b.length; j += 1) {
      const custo = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(nova[j - 1] + 1, linha[j] + 1, linha[j - 1] + custo);
      nova.push(v);
      melhor = Math.min(melhor, v);
    }
    if (melhor > limite) return limite + 1;
    linha = nova;
  }
  return linha[b.length];
}

/**
 * Soa como? Tolerância proporcional ao tamanho — palavra curta não ganha
 * folga, senão "oct" casaria com "ocra" e a defesa viraria enfeite.
 */
export function soaParecido(termo: string, palavra: string): boolean {
  const a = fonetico(termo);
  const b = fonetico(palavra);
  if (a === b) return true;
  if (a.length < 4 || b.length < 4) return false;
  const limite = Math.min(a.length, b.length) >= 6 ? 2 : 1;
  return distancia(a, b, limite) <= limite;
}

/** Palavras que uma ditada de refração sempre carrega. */
const PALAVRAS_REFRACAO = [
  'refra', 'esferic', 'cilindric', 'eixo', 'adicao', 'grau', 'dioptria', 'oculos', 'lente',
];

/**
 * Valor de exame se sustenta na fala? Só números são conferidos (texto como
 * "sem alterações" fica com a âncora do módulo). Fala sem dígito nenhum
 * (transcrição por extenso, "quatorze") não acusa: não dá pra provar nada.
 */
function valorNaFala(valor: string, fala: string): boolean {
  const nums = valor.match(/\d+(?:[.,]\d+)?/g);
  if (!nums || !/\d/.test(fala)) return true;
  const falaNums = new Set(
    (fala.match(/\d+(?:[.,]\d+)?/g) ?? []).flatMap((n) => {
      const x = n.replace(',', '.');
      return [x, String(parseFloat(x))];
    }),
  );
  return nums.every((n) => {
    const x = n.replace(',', '.');
    return falaNums.has(x) || falaNums.has(String(parseFloat(x)));
  });
}

/**
 * Apelido curto só de letras/dígitos casa a palavra INTEIRA ("av" não casa
 * "avaliação", "lio" não casa "folio"); o resto casa como trecho ("20/",
 * "e/d", "nervo optico"). Até 02/10/2026 o "20/" caía na regra da palavra
 * inteira e nunca casava.
 */
function citaNaFala(apelido: string, fala: string, palavras: Set<string>): boolean {
  return /^[a-z0-9]{1,3}$/.test(apelido) ? palavras.has(apelido) : fala.includes(apelido);
}

/**
 * Módulos do card "Exames do atendimento" que a fala NÃO sustenta: nem o
 * nome/sigla do exame, nem um apelido comum ("pressão" → PIO), nem a
 * estrutura do achado ("pterígio" → biomicroscopia, "escavação" →
 * fundoscopia) foi dito, ou algum número do módulo não aparece no que foi
 * transcrito.
 *
 * Achado em TEXTO (campo "achados") se sustenta pela estrutura dita, sem
 * conferir número (02/10/2026): o grau vem na escala do catálogo ("++/4+"
 * de "duas cruzes"), muitas vezes por extenso na transcrição, e a conferência
 * de número tirava do card o achado inteiro. Número de MEDIDA (PIO, E/D,
 * paquimetria…) continua conferido.
 *
 * `jaNaFila`: módulos que já estão no card aberto. Correção ditada ("não,
 * no direito é vinte") não repete o nome do exame — então, pra esses, basta
 * o NÚMERO estar na fala (01/10/2026, achado no teste de bancada do Ao vivo).
 */
export function unanchoredExamModules(
  draft: Extract<VoiceDraft, { kind: 'examesAtendimento' }>,
  transcricao: string,
  jaNaFila: readonly string[] = [],
): string[] {
  const fala = norm(transcricao || '');
  if (!fala.trim()) return [];
  const palavras = palavrasDaFala(fala);
  return Object.entries(draft.fill)
    .filter(([moduleId, fields]) => {
      const nome = examModuleLabel(moduleId);
      const apelidos = [...(EXAM_MODULE_ALIASES[moduleId] ?? []), ...(EXAM_FINDING_WORDS[moduleId] ?? [])];
      const citado =
        jaNaFila.includes(moduleId) ||
        termosSignificativos(nome).some((t) => apareceNaFala(t, fala)) ||
        siglas(nome).some((sig) => palavras.has(sig)) ||
        apelidos.some((a) => citaNaFala(a, fala, palavras));
      const valores = Object.entries(fields)
        .filter(([key]) => !isTextFindingField(moduleId, key))
        .flatMap(([, c]) => [c.OD, c.OE, c.mono])
        .filter((v): v is string => !!v);
      return !citado || !valores.every((v) => valorNaFala(v, fala));
    })
    .map(([id]) => id);
}

/**
 * Gera as pendências de ancoragem de um card.
 *
 * Devolve lista vazia quando o documento se sustenta na fala. Quando a
 * transcrição não veio (modelo antigo, campo ausente), devolve vazio também —
 * não dá pra acusar sem prova.
 */
export function groundingGaps(draft: VoiceDraft, transcricao: string, examesNaFila: readonly string[] = []): VoiceGap[] {
  const fala = norm(transcricao || '');
  if (!fala.trim()) return [];
  const palavras = palavrasDaFala(fala);

  /** O nome se sustenta na fala — por palavra significativa OU por sigla. */
  const ancora = (nome: string): boolean => {
    const termos = termosSignificativos(nome);
    if (termos.length === 0) return true;
    if (termos.some((t) => apareceNaFala(t, fala))) return true;
    if (siglas(nome).some((sig) => palavras.has(sig))) return true;
    // Último recurso: o banco corrigiu a fonética do que foi dito.
    return termos.some((t) => [...palavras].some((w) => soaParecido(t, w)));
  };

  const naoConfere = (field: string, label: string, ouvido: string): VoiceGap => ({
    field,
    label,
    heard: ouvido,
    issue: 'not_heard',
  });

  switch (draft.kind) {
    case 'receitaOculos': {
      const falouDeRefracao = PALAVRAS_REFRACAO.some((p) => fala.includes(p));
      return falouDeRefracao
        ? []
        : [naoConfere('oculos.ancoragem', 'Receita de óculos não confere com o ditado', '')];
    }

    case 'receitaMedicamentos':
      return draft.items.flatMap((item, i) => {
        const nome = item.match.value?.nome ?? item.match.heard;
        if (!nome.trim()) return [];
        return ancora(nome)
          ? []
          // `heard` vazio de propósito: o nome vem do catálogo, NÃO da fala.
          // Mostrá-lo como "ouvi …" dizia o contrário do que a pendência acusa
          // (visto no card em 20/09/2026).
          : [naoConfere(`medicamentos[${i}].ancoragem`, `"${nome}" não aparece no que foi transcrito`, '')];
      });

    case 'solicitacaoExames':
      return [
        ...draft.exams.flatMap((exame, i) =>
          ancora(exame.nome)
            ? []
            : [naoConfere(`exames[${i}].ancoragem`, `"${exame.nome}" não aparece no que foi transcrito`, '')]),
        ...draft.surgeries.flatMap((s, i) =>
          ancora(s.texto)
            ? []
            : [naoConfere(`cirurgias[${i}].ancoragem`, 'A cirurgia não confere com o ditado', '')]),
      ];

    case 'examesAtendimento':
      return unanchoredExamModules(draft, transcricao, examesNaFila).map((id) =>
        naoConfere(`examesAtendimento.${id}.ancoragem`, `"${examModuleLabel(id)}" não confere com o ditado — não foi para os exames`, ''));

    case 'atestadoLaudo': {
      // O documento tem que ter sido PEDIDO: uma palavra de documento
      // ("atestado", "declaração", "laudo"…) ou do título do modelo na fala.
      const falouDeDocumento = PALAVRAS_DOCUMENTO.some((p) => fala.includes(p));
      const titulo = draft.modelo?.title ?? draft.pedido;
      return falouDeDocumento || (titulo.trim() && ancora(titulo))
        ? []
        : [naoConfere('atestadoLaudo.ancoragem', `"${draft.label}" não confere com o ditado`, '')];
    }
  }
}

/** Palavras que um pedido de atestado/declaração/laudo sempre carrega (sem acento). */
const PALAVRAS_DOCUMENTO = [
  'atestad', 'declara', 'laudo', 'relatorio', 'encaminh', 'comparec', 'afastament', 'repouso', 'termo',
];

/**
 * Aplica a ancoragem sobre os cards, acrescentando as pendências encontradas.
 *
 * Card com pendência de ancoragem não imprime — igual a qualquer outra lacuna.
 */
export function applyGrounding(
  drafts: VoiceDraft[],
  transcricao: string,
  /** Módulos de exame já no card aberto (ver `unanchoredExamModules`). */
  examesNaFila: readonly string[] = [],
): VoiceDraft[] {
  return drafts.flatMap((draft) => {
    const extra = groundingGaps(draft, transcricao, examesNaFila);
    if (extra.length === 0) return [draft];
    if (draft.kind === 'examesAtendimento') {
      // Valor de exame não sustentado não entra nos módulos (iria direto pro
      // prontuário sem papel pra conferir). Sai do card; a pendência fica.
      const fill = dropExamModules(draft.fill, unanchoredExamModules(draft, transcricao, examesNaFila));
      return [{ ...draft, fill, gaps: [...draft.gaps, ...extra] }];
    }
    return [{ ...draft, gaps: [...draft.gaps, ...extra] }];
  });
}

/** A transcrição que o modelo devolveu, quando devolveu. */
export function readTranscript(structured: unknown): string {
  const t = (structured as { transcricao?: unknown } | null)?.transcricao;
  return typeof t === 'string' ? t.trim() : '';
}
